import React, { useState } from 'react';
import { ChevronRight, Search } from 'lucide-react';
import { Input } from '@client/components/ui/input';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@client/components/ui/tooltip';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@client/components/ui/table';
import { USAGE_METRICS_CONFIG } from '@shared/config/usageMetricsConfig';

interface AdvisorUsageTableProps {
  advisors: AdvisorUsageData[];
}

export function AdvisorUsageTable({ advisors }: AdvisorUsageTableProps) {
  const [expandedRowId, setExpandedRowId] = useState<string | null>(null);
  const [searchText, setSearchText] = useState('');

  const toggleRow = (advisorId: string) => {
    setExpandedRowId(expandedRowId === advisorId ? null : advisorId);
  };

  const filteredAdvisors = advisors.filter(advisor => {
    if (!searchText) return true;
    const search = searchText.toLowerCase();
    return (
      advisor.advisorName.toLowerCase().includes(search) ||
      advisor.advisorEmail.toLowerCase().includes(search) ||
      advisor.organizationName.toLowerCase().includes(search)
    );
  });

  return (
    <div className="space-y-4">
      {/* Search Input */}
      <div className="relative max-w-sm">
        <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-gray-400" />
        <Input
          type="text"
          placeholder="Search advisors..."
          value={searchText}
          onChange={e => setSearchText(e.target.value)}
          className="pl-10"
        />
      </div>

      {/* Table */}
      <div className="overflow-hidden rounded-lg border border-gray-200 bg-white">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Advisor Name</TableHead>
              <TableHead>Organization</TableHead>
              <TableHead>Email</TableHead>
              <TableHead>Account Age</TableHead>
              <TableHead>Total Logins</TableHead>
              <TableHead className="text-right">Clients Created</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {filteredAdvisors.length === 0 ? (
              <TableRow>
                <TableCell
                  colSpan={6}
                  className="py-12 text-center"
                >
                  <p className="text-gray-500">
                    {searchText
                      ? 'No advisors found matching your search.'
                      : 'No advisor data available.'}
                  </p>
                </TableCell>
              </TableRow>
            ) : (
              filteredAdvisors.map((advisor, index) => {
                const isExpanded = expandedRowId === advisor.advisorId;
                const isLastRow = index === filteredAdvisors.length - 1;

                return (
                  <React.Fragment key={advisor.advisorId}>
                    {/* Main Row */}
                    <TableRow
                      className="cursor-pointer transition-colors hover:bg-gray-100"
                      onClick={() => toggleRow(advisor.advisorId)}
                    >
                      <TableCell>
                        <div className="flex items-center gap-3">
                          <div className="hover:bg-primary-100 flex h-8 w-8 items-center justify-center rounded-full transition-colors">
                            <ChevronRight
                              className={`h-5 w-5 text-gray-600 transition-transform ${
                                isExpanded ? 'rotate-90' : ''
                              }`}
                            />
                          </div>
                          <span className="font-medium text-gray-900">{advisor.advisorName}</span>
                        </div>
                      </TableCell>
                      <TableCell className="font-medium text-gray-900">
                        {advisor.organizationName}
                      </TableCell>
                      <TableCell className="text-gray-500">{advisor.advisorEmail}</TableCell>
                      <TableCell className="text-gray-900">{advisor.accountAge}</TableCell>
                      <TableCell className="text-gray-900">
                        {advisor.usageMetrics.totalAdvisorLogins}
                      </TableCell>
                      <TableCell className="text-right font-semibold text-gray-900">
                        {advisor.usageMetrics.totalInvitedClients}
                      </TableCell>
                    </TableRow>

                    {/* Expanded Metrics Row */}
                    {isExpanded && (
                      <TableRow>
                        <TableCell
                          colSpan={6}
                          className="bg-gray-50 px-6 py-6"
                        >
                          <div className="ml-8">
                            <h4 className="mb-4 text-sm font-bold text-gray-900">Usage Metrics</h4>
                            <TooltipProvider>
                              <div className="grid grid-cols-2 gap-6 md:grid-cols-3 lg:grid-cols-6">
                                {USAGE_METRICS_CONFIG.map(metricDef => (
                                  <MetricItem
                                    key={metricDef.key}
                                    label={metricDef.label}
                                    value={advisor.usageMetrics[metricDef.key]}
                                    description={metricDef.description}
                                    format={metricDef.format}
                                  />
                                ))}
                              </div>
                            </TooltipProvider>
                          </div>
                        </TableCell>
                      </TableRow>
                    )}
                  </React.Fragment>
                );
              })
            )}
          </TableBody>
        </Table>
      </div>
    </div>
  );
}

function MetricItem({
  label,
  value,
  description,
  format,
}: {
  label: string;
  value: string | number;
  description?: string;
  format?: 'number' | 'currency' | 'percentage';
}) {
  const formattedValue = React.useMemo(() => {
    if (typeof value === 'string') return value;
    if (format === 'currency') {
      return `$${value.toLocaleString()}`;
    }
    if (format === 'percentage') {
      return `${value}%`;
    }
    return value.toLocaleString();
  }, [value, format]);

  return (
    <Tooltip>
      <TooltipTrigger asChild>
        <div className="cursor-help rounded-lg border border-gray-200 bg-white p-3">
          <p className="mb-1 text-xs text-gray-500">{label}</p>
          <p className="text-lg font-bold text-gray-900">{formattedValue}</p>
        </div>
      </TooltipTrigger>
      {description && (
        <TooltipContent>
          <p>{description}</p>
        </TooltipContent>
      )}
    </Tooltip>
  );
}
